import React, {useEffect, useRef, useState} from "react";
import {IColor} from "../interfaces";

interface ColorSelectDropdownProps {
    colors: IColor[]
    selectedColorId?: string
    disabled?: boolean
    onChange: (colorId: string) => void
}

const ColorSelectDropdown: React.FC<ColorSelectDropdownProps> = ({colors, selectedColorId, disabled, onChange}) => {
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)

    const selected = colors.find(c => (c.id as string) === selectedColorId)

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleSelect = (colorId: string) => {
        setOpen(false)
        onChange(colorId)
    }

    return (
        <div ref={ref} style={{position: 'relative', display: 'inline-block'}}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen(o => !o)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '3px 10px 3px 6px',
                    border: '1px solid #ccc',
                    borderRadius: 6,
                    background: '#fff',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    fontSize: 13,
                    opacity: disabled ? 0.6 : 1,
                    minWidth: 140,
                }}
            >
                <ColorSwatch code={selected?.code} />
                <span style={{flex: 1, textAlign: 'left'}}>
                    {selected ? `${selected.name} (${selected.code})` : 'Change color…'}
                </span>
                <span style={{fontSize: 10, color: '#888', marginLeft: 4}}>▾</span>
            </button>

            {open && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    zIndex: 1000,
                    background: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    minWidth: 180,
                    maxHeight: 240,
                    overflowY: 'auto',
                    marginTop: 2,
                }}>
                    {colors.map(c => (
                        <div
                            key={c.id as string}
                            onClick={() => handleSelect(c.id as string)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '7px 12px',
                                cursor: 'pointer',
                                fontSize: 13,
                                background: (c.id as string) === selectedColorId ? '#f0f0f0' : 'transparent',
                            }}
                            onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
                            onMouseLeave={e => (e.currentTarget.style.background = (c.id as string) === selectedColorId ? '#f0f0f0' : 'transparent')}
                        >
                            <ColorSwatch code={c.code} />
                            <span>{c.name}</span>
                            <span style={{color: '#aaa', fontSize: 11, marginLeft: 'auto'}}>{c.code}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

const ColorSwatch: React.FC<{code?: string}> = ({code}) => (
    <span style={{
        display: 'inline-block',
        width: 14,
        height: 14,
        borderRadius: '50%',
        backgroundColor: code || '#ccc',
        border: '1px solid rgba(0,0,0,0.18)',
        flexShrink: 0,
    }} />
)

export default ColorSelectDropdown
